import { useEffect, useState } from "react";
import { ClipboardCheck, Send, RotateCcw, Eye, CheckCircle2, Loader2, Undo2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/constants";
import { useBilling } from "@/context/BillingContext";
import { confirm } from "@/components/tracker/confirm";
import { UpgradeHint } from "@/components/tracker/PlanGate";
import { guardReview, PartialTag } from "@/components/tracker/reviewGuard";

const fmtDateTime = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "—");

export default function ReviewPanel({ task, canEdit = false, onSaved, onTaskPatched, liveStatus, liveProgress, liveTask }) {
  const { user } = useAuth();
  const has = useBilling()?.has || (() => false);
  const [status, setStatus] = useState(task?.status ?? "NOT STARTED");
  const [rv, setRv] = useState({ reviewer_id: null, reviewer_name: null, review_cycles: 0, review_history: [] });
  const [members, setMembers] = useState([]);
  const [pickReviewer, setPickReviewer] = useState("");
  const [reviewMode, setReviewMode] = useState(null); // null | "send" | "changes"
  const [changeComment, setChangeComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [editCycles, setEditCycles] = useState(false);
  const [cyclesInput, setCyclesInput] = useState("");
  const [progress, setProgress] = useState(task?.progress ?? 0);

  useEffect(() => {
    setStatus(task?.status ?? "NOT STARTED");
    setProgress(task?.progress ?? 0);
    setRv({
      reviewer_id: task?.reviewer_id ?? null,
      reviewer_name: task?.reviewer_name ?? null,
      review_cycles: task?.review_cycles ?? 0,
      review_history: task?.review_history ?? [],
    });
    setReviewMode(null);
    setChangeComment("");
    setPickReviewer("");
    setEditCycles(false);
    // reset the panel only when a different task opens; live updates must not wipe in-progress edits
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task?.id]);

  useEffect(() => { if (liveStatus) setStatus(liveStatus); }, [liveStatus]);
  useEffect(() => {
    if (task?.id && task.status === "REVIEW" && task.reviewer_id && task.reviewer_id === user?.id) {
      api.post(`/tasks/${task.id}/review/seen`).catch(() => {});
    }
  }, [task?.id, task?.status, task?.reviewer_id, user?.id]);
  useEffect(() => {
    if (!liveTask) return;
    setRv({
      reviewer_id: liveTask.reviewer_id ?? null,
      reviewer_name: liveTask.reviewer_name ?? null,
      review_cycles: liveTask.review_cycles ?? 0,
      review_history: liveTask.review_history ?? [],
    });
  }, [liveTask]);
  useEffect(() => { if (typeof liveProgress === "number") setProgress(liveProgress); }, [liveProgress]);

  useEffect(() => {
    if (!task?.project_id) return;
    api.get(`/projects/${task.project_id}/members`).then((r) => setMembers(r.data || [])).catch(() => setMembers([]));
  }, [task?.project_id]);

  if (!task) return null;

  const amLead = members.some((m) => m.id === user?.id && m.project_role === "lead");
  const reviewerOptions = members.filter((m) => m.project_role === "reviewer" || m.project_role === "lead");
  const canReview = !!user && (user.id === rv.reviewer_id || user.role === "admin" || amLead);
  const amGroupLead = !!task.group_id && members.some((m) => m.id === user?.id && (m.group_lead_of || []).includes(task.group_id));
  const canAdjustCycles = !!user && (user.role === "admin" || amLead || amGroupLead) && has("review_cycles");
  const reviewOn = has("review");
  const inReview = status === "REVIEW";
  const canWithdraw = !!user && (user.role === "admin" || amLead);
  const hist = rv.review_history;
  const lastType = hist[hist.length - 1]?.type;
  const restorable = canWithdraw && !!rv.reviewer_id && (
    (!inReview && status !== "DONE" && lastType === "withdrawn") ||
    (inReview && lastType === "sent" && hist[hist.length - 2]?.type === "withdrawn"));

  if (!(canEdit || inReview || rv.review_history.length > 0)) return null;
  if (!reviewOn) {
    return (
      <div className="mt-4" data-testid="task-detail-review-locked">
        <UpgradeHint label="Review workflow" plan="Team" testid="task-detail-review-upgrade" className="w-full" />
      </div>
    );
  }

  const apply = (data) => {
    setStatus(data.status);
    if (typeof data.progress === "number") setProgress(data.progress);
    setRv({
      reviewer_id: data.reviewer_id ?? null,
      reviewer_name: data.reviewer_name ?? null,
      review_cycles: data.review_cycles ?? 0,
      review_history: data.review_history ?? [],
    });
    onTaskPatched?.(data);
    onSaved?.(data);
  };

  const saveCycles = async () => {
    const n = parseInt(cyclesInput, 10);
    if (Number.isNaN(n) || n < 0) {
      toast.error("Enter a whole number (0 or more)");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/tasks/${task.id}/review/set-cycles`, { cycles: n });
      apply(data);
      setEditCycles(false);
      toast.success("Review cycle count updated");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const sendForReview = async () => {
    if (!pickReviewer) return;
    const choice = await guardReview({ progress });
    if (!choice) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/tasks/${task.id}/review/request`, { reviewer_id: pickReviewer, set_complete: choice === "complete" });
      apply(data);
      setReviewMode(null);
      setPickReviewer("");
      toast.success("Sent for review");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const reassignReviewer = async () => {
    if (!pickReviewer || pickReviewer === rv.reviewer_id) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/tasks/${task.id}/review/reassign`, { reviewer_id: pickReviewer });
      apply(data);
      setReviewMode(null);
      setPickReviewer("");
      toast.success("Reviewer updated");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const withdraw = async () => {
    const reason = await confirm({
      title: "Withdraw from review?",
      message: `The task goes back to In progress and ${rv.reviewer_name || "the reviewer"} is notified. Add a short reason — it's saved in the review history. If this was a mistake you can restore it with its original review date.`,
      confirmText: "Withdraw",
      tone: "neutral",
      input: { placeholder: "e.g. Sent by mistake, missing drawings…" },
    });
    if (!reason) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/tasks/${task.id}/review/withdraw`, { reason });
      apply(data);
      toast.success("Withdrawn from review");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const restore = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/tasks/${task.id}/review/restore`);
      apply(data);
      toast.success("Review restored — original review date kept");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const approveReview = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/tasks/${task.id}/review/approve`);
      apply(data);
      toast.success(data.status === "DONE" ? "Approved — task marked done" : `Partial review approved — back to In progress at ${data.progress}%`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const submitChanges = async () => {
    if (!changeComment.trim()) {
      toast.error("Please add feedback explaining what needs to change");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/tasks/${task.id}/review/request-changes`, { comment: changeComment.trim() });
      apply(data);
      setReviewMode(null);
      setChangeComment("");
      toast.success("Changes requested — sent back to the owner");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-4 rounded-2xl border border-white/5 bg-white/[0.02] p-4 sm:p-5" data-testid="task-detail-review-panel">
      <div className="mb-3 flex items-center justify-between">
        <p className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.2em] text-white/40">
          <ClipboardCheck className="h-3.5 w-3.5" />Review
        </p>
        {editCycles ? (
          <div className="flex items-center gap-1.5" data-testid="review-cycles-editor">
            <input
              type="number"
              min="0"
              value={cyclesInput}
              onChange={(e) => setCyclesInput(e.target.value)}
              autoFocus
              className="w-14 rounded-lg border border-[#90C2E7]/40 bg-white/5 px-2 py-1 text-xs font-bold text-white outline-none focus:border-[#90C2E7]"
              data-testid="review-cycles-input"
            />
            <button
              onClick={saveCycles}
              disabled={busy}
              className="rounded-full bg-[#90C2E7] px-2.5 py-1 text-[10px] font-black text-black transition-colors duration-200 hover:brightness-110 disabled:opacity-50"
              data-testid="review-cycles-save"
            >
              {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : "Save"}
            </button>
            <button
              onClick={() => setEditCycles(false)}
              className="rounded-full border border-white/15 px-2.5 py-1 text-[10px] font-bold text-white/55 transition-colors duration-200 hover:text-white"
              data-testid="review-cycles-cancel"
            >
              Cancel
            </button>
          </div>
        ) : rv.review_cycles > 0 ? (
          <button
            onClick={() => { if (canAdjustCycles) { setCyclesInput(String(rv.review_cycles)); setEditCycles(true); } }}
            disabled={!canAdjustCycles}
            className={`flex items-center gap-1 rounded-full border border-[#90C2E7]/30 bg-[#90C2E7]/10 px-2.5 py-0.5 text-[10px] font-black uppercase tracking-wider text-[#90C2E7] transition-colors duration-200 ${canAdjustCycles ? "cursor-pointer hover:border-[#90C2E7]/60 hover:bg-[#90C2E7]/20" : "cursor-default"}`}
            data-testid="task-detail-review-cycles"
            title={canAdjustCycles ? `This task has entered review ${rv.review_cycles} time(s) — click to correct` : `This task has entered review ${rv.review_cycles} time(s)`}
          >
            <RotateCcw className="h-3 w-3" />
            {rv.review_cycles}× in review
          </button>
        ) : canAdjustCycles && (canEdit || inReview || rv.review_history.length > 0) ? (
          <button
            onClick={() => { setCyclesInput("0"); setEditCycles(true); }}
            className="flex items-center gap-1 rounded-full border border-white/10 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white/40 transition-colors duration-200 hover:border-[#90C2E7]/40 hover:text-[#90C2E7]"
            data-testid="task-detail-review-cycles-set"
            title="Set the review cycle count"
          >
            <RotateCcw className="h-3 w-3" />
            Cycles
          </button>
        ) : null}
      </div>

      {inReview && (
        <div className="mb-3 flex items-center gap-1.5 text-sm font-semibold text-white/90" data-testid="task-detail-reviewer">
          <Eye className="h-3.5 w-3.5 shrink-0 text-[#90C2E7]" />
          <span className="text-white/50">Reviewer:</span> {rv.reviewer_name || "Unassigned"}
          {progress < 100 && <PartialTag progress={progress} className="ml-1" testid="task-detail-partial" />}
          {reviewOn && (canEdit || canReview) && reviewMode !== "reassign" && (
            <button
              onClick={() => { setReviewMode("reassign"); setPickReviewer(rv.reviewer_id || ""); }}
              className="ml-1.5 rounded-full border border-white/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white/55 transition-colors duration-200 hover:border-[#90C2E7]/50 hover:text-[#90C2E7]"
              data-testid="task-detail-change-reviewer"
            >
              Change
            </button>
          )}
          {reviewOn && canWithdraw && user?.id !== rv.reviewer_id && reviewMode !== "reassign" && (
            <button
              onClick={withdraw}
              disabled={busy}
              className="ml-auto inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white/40 transition-colors duration-200 hover:text-[#FFB020] disabled:opacity-50"
              title="Pull this task back to In progress"
              data-testid="task-detail-withdraw-review"
            >
              <Undo2 className="h-3 w-3" /> Withdraw
            </button>
          )}
        </div>
      )}

      {reviewOn && restorable && (
        <div className="mb-3 flex flex-wrap items-center gap-3 rounded-xl border border-[#FFB020]/25 bg-[#FFB020]/[0.06] px-3 py-2.5" data-testid="task-detail-restore-review-banner">
          <Undo2 className="h-4 w-4 shrink-0 text-[#FFB020]" />
          <p className="flex-1 text-xs text-white/65">
            {inReview ? "This was withdrawn and re-sent, so its review clock restarted." : `Withdrawn from review by ${hist[hist.length - 1]?.by_name || "someone"}.`}{" "}
            Restore to keep the original review date.
          </p>
          <button
            onClick={restore}
            disabled={busy}
            className="inline-flex items-center gap-1.5 rounded-full bg-[#FFB020] px-3.5 py-1.5 text-xs font-bold text-black transition-colors duration-200 hover:brightness-110 disabled:opacity-50"
            data-testid="task-detail-restore-review"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />} Restore review
          </button>
        </div>
      )}

      {inReview && reviewMode === "reassign" && (
        <div className="mb-3 space-y-2.5" data-testid="task-detail-reassign-form">
          <select
            value={pickReviewer}
            onChange={(e) => setPickReviewer(e.target.value)}
            className="w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2.5 text-sm font-semibold text-white outline-none transition-colors duration-200 focus:border-[#90C2E7]/50 [&>option]:bg-[#0A120D]"
            data-testid="task-detail-reassign-select"
          >
            <option value="">Choose a reviewer…</option>
            {reviewerOptions.map((m) => (
              <option key={m.id} value={m.id}>{m.name}{m.project_role === "lead" ? " (lead)" : ""}</option>
            ))}
          </select>
          {reviewerOptions.length === 0 && (
            <p className="text-[11px] leading-relaxed text-white/35">
              No reviewers on this project yet. Add one under Projects → Team → Reviewer.
            </p>
          )}
          <div className="grid grid-cols-[1fr_7rem] gap-2">
            <button
              onClick={reassignReviewer}
              disabled={busy || !pickReviewer || pickReviewer === rv.reviewer_id}
              className="flex items-center justify-center gap-1.5 rounded-full bg-[#90C2E7] py-2.5 text-xs font-bold text-black transition-colors duration-200 hover:brightness-110 disabled:opacity-50"
              data-testid="task-detail-reassign-submit"
            >
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Eye className="h-3.5 w-3.5" />}
              Update reviewer
            </button>
            <button
              onClick={() => { setReviewMode(null); setPickReviewer(""); }}
              className="rounded-full border border-white/15 py-2.5 text-xs font-bold text-white/60 transition-colors duration-200 hover:border-white/30 hover:text-white"
              data-testid="task-detail-reassign-cancel"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {inReview && canReview && reviewMode !== "changes" && reviewMode !== "reassign" && (
        <div className="grid grid-cols-2 gap-2" data-testid="task-detail-review-actions">
          <button
            onClick={approveReview}
            disabled={busy}
            className="flex items-center justify-center gap-1.5 rounded-full bg-[#00FF66] py-2.5 text-xs font-bold text-black shadow-[0_0_20px_rgba(0,255,102,0.25)] transition-colors duration-200 hover:bg-[#00cc52] disabled:opacity-60"
            data-testid="task-detail-review-approve"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
            {progress < 100 ? "Approve partial" : "Approve"}
          </button>
          <button
            onClick={() => setReviewMode("changes")}
            disabled={busy}
            className="flex items-center justify-center gap-1.5 rounded-full border border-[#FFB020]/40 bg-[#FFB020]/10 py-2.5 text-xs font-bold text-[#FFB020] transition-colors duration-200 hover:bg-[#FFB020]/20 disabled:opacity-60"
            data-testid="task-detail-review-request-changes"
          >
            <RotateCcw className="h-3.5 w-3.5" />
            Request changes
          </button>
        </div>
      )}

      {inReview && canReview && reviewMode === "changes" && (
        <div className="space-y-2.5" data-testid="task-detail-changes-form">
          <textarea
            value={changeComment}
            onChange={(e) => setChangeComment(e.target.value)}
            rows={3}
            autoFocus
            placeholder="What needs to change? This is sent to the task owner…"
            className="w-full resize-none rounded-xl border border-white/10 bg-white/5 px-3 py-2.5 text-sm text-white outline-none transition-colors duration-200 placeholder:text-white/30 focus:border-[#FFB020]/50"
            data-testid="task-detail-changes-input"
          />
          <div className="grid grid-cols-[1fr_7rem] gap-2">
            <button
              onClick={submitChanges}
              disabled={busy || !changeComment.trim()}
              className="flex items-center justify-center gap-1.5 rounded-full bg-[#FFB020] py-2.5 text-xs font-bold text-black transition-colors duration-200 hover:brightness-110 disabled:opacity-50"
              data-testid="task-detail-changes-submit"
            >
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />}
              Send back
            </button>
            <button
              onClick={() => { setReviewMode(null); setChangeComment(""); }}
              className="rounded-full border border-white/15 py-2.5 text-xs font-bold text-white/60 transition-colors duration-200 hover:border-white/30 hover:text-white"
              data-testid="task-detail-changes-cancel"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {!inReview && canEdit && !reviewOn && (
        <UpgradeHint label="Review workflow" plan="Team" testid="task-detail-review-upgrade" className="w-full" />
      )}
      {!inReview && canEdit && reviewOn && reviewMode !== "send" && (
        <button
          onClick={() => setReviewMode("send")}
          className="flex w-full items-center justify-center gap-2 rounded-full border border-[#90C2E7]/30 bg-[#90C2E7]/10 py-2.5 text-xs font-bold text-[#90C2E7] transition-colors duration-200 hover:bg-[#90C2E7]/20"
          data-testid="task-detail-send-for-review"
        >
          <Send className="h-4 w-4" />
          Send for review
        </button>
      )}
      {!inReview && canEdit && reviewOn && reviewMode === "send" && (
        <div className="space-y-2.5" data-testid="task-detail-send-form">
          <select
            value={pickReviewer}
            onChange={(e) => setPickReviewer(e.target.value)}
            className="w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2.5 text-sm font-semibold text-white outline-none transition-colors duration-200 focus:border-[#90C2E7]/50 [&>option]:bg-[#0A120D]"
            data-testid="task-detail-reviewer-select"
          >
            <option value="">Choose a reviewer…</option>
            {reviewerOptions.map((m) => (
              <option key={m.id} value={m.id}>{m.name}{m.project_role === "lead" ? " (lead)" : ""}</option>
            ))}
          </select>
          {reviewerOptions.length === 0 && (
            <p className="text-[11px] leading-relaxed text-white/35" data-testid="task-detail-no-reviewers">
              No reviewers on this project yet. Add one under Projects → Team → Reviewer.
            </p>
          )}
          <div className="grid grid-cols-[1fr_7rem] gap-2">
            <button
              onClick={sendForReview}
              disabled={busy || !pickReviewer}
              className="flex items-center justify-center gap-1.5 rounded-full bg-[#90C2E7] py-2.5 text-xs font-bold text-black transition-colors duration-200 hover:brightness-110 disabled:opacity-50"
              data-testid="task-detail-send-submit"
            >
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
              Send
            </button>
            <button
              onClick={() => { setReviewMode(null); setPickReviewer(""); }}
              className="rounded-full border border-white/15 py-2.5 text-xs font-bold text-white/60 transition-colors duration-200 hover:border-white/30 hover:text-white"
              data-testid="task-detail-send-cancel"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {rv.review_history.length > 0 && (
        <ul className="mt-3 space-y-2 border-t border-white/5 pt-3" data-testid="task-detail-review-history">
          {[...rv.review_history].reverse().map((h, idx) => {
            const meta = h.type === "approved"
              ? { icon: CheckCircle2, color: "#00FF66", label: `Approved by ${h.by_name}` }
              : h.type === "changes_requested"
              ? { icon: RotateCcw, color: "#FFB020", label: `Changes requested by ${h.by_name}` }
              : h.type === "reassigned"
              ? { icon: Eye, color: "#90C2E7", label: `Reviewer changed to ${h.reviewer_name || "—"} by ${h.by_name}` }
              : h.type === "approved_partial"
              ? { icon: CheckCircle2, color: "#FFB020", label: `Partial review (${h.progress}%) approved by ${h.by_name}` }
              : h.type === "withdrawn"
              ? { icon: Undo2, color: "#FFB020", label: `Withdrawn from review by ${h.by_name}` }
              : h.type === "restored"
              ? { icon: RotateCcw, color: "#00FF66", label: `Review restored with its original date by ${h.by_name}` }
              : h.type === "cycles_adjusted"
              ? { icon: RotateCcw, color: "#90C2E7", label: `Review cycles corrected ${h.from_cycles}→${h.to_cycles} by ${h.by_name}` }
              : { icon: Send, color: "#90C2E7", label: `Sent for review to ${h.reviewer_name || "—"} by ${h.by_name}` };
            const Icon = meta.icon;
            return (
              <li key={idx} className="flex gap-2.5 text-xs" data-testid={`task-detail-review-event-${h.type}`}>
                <Icon className="mt-0.5 h-3.5 w-3.5 shrink-0" style={{ color: meta.color }} />
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-white/80">{meta.label}</p>
                  {h.comment && <p className="mt-0.5 whitespace-pre-wrap leading-relaxed text-white/50">“{h.comment}”</p>}
                  {h.reason && <p className="mt-0.5 whitespace-pre-wrap leading-relaxed text-white/50" data-testid="task-detail-withdraw-reason">Reason: “{h.reason}”</p>}
                  <p className="mt-0.5 text-[10px] uppercase tracking-wider text-white/30">{fmtDateTime(h.at)}</p>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
